if (!customElements.get('video-player')) {
  customElements.define(
    'video-player',
    class VideoPlayer extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('[data-vp-video]');
        if (!this.video) return;

        this.frame = this.querySelector('[data-vp-frame]');
        this.poster = this.querySelector('[data-vp-poster]');
        this.unmuteSurface = this.querySelector('[data-vp-unmute]');
        this.playButton = this.querySelector('[data-vp-play]');
        this.rewindButton = this.querySelector('[data-vp-rewind]');
        this.forwardButton = this.querySelector('[data-vp-forward]');
        this.volumeButton = this.querySelector('[data-vp-volume]');
        this.fullscreenButton = this.querySelector('[data-vp-fullscreen]');
        this.progress = this.querySelector('[data-vp-progress]');
        this.played = this.querySelector('[data-vp-played]');
        this.buffer = this.querySelector('[data-vp-buffer]');
        this.handle = this.querySelector('[data-vp-handle]');

        this.skipBy = 10;
        this.hideDelay = 2500;
        this.hideTimer = null;
        this.scrubbing = false;
        this.autoplay = this.hasAttribute('data-autoplay');
        this.persistControls = this.hasAttribute('data-controls-persist');
        this.ctaMode = this.hasAttribute('data-cta');

        this.onPlay = this.onPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onTimeUpdate = this.onTimeUpdate.bind(this);
        this.onProgress = this.onProgress.bind(this);
        this.onVolumeChange = this.onVolumeChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPointerActivity = this.onPointerActivity.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onScrubStart = this.onScrubStart.bind(this);
        this.onScrubMove = this.onScrubMove.bind(this);
        this.onScrubEnd = this.onScrubEnd.bind(this);
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.onOtherPlayerStarted = this.onOtherPlayerStarted.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
        this.onFirstInteraction = this.onFirstInteraction.bind(this);

        this.bindEvents();
        this.observeViewport();
        this.syncPlayState();
        this.syncVolumeState();

        if (!this.autoplay) this.classList.add('is-idle');
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.hideTimer) clearTimeout(this.hideTimer);
        document.removeEventListener('vplayer:play', this.onOtherPlayerStarted);
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
        window.removeEventListener('pointermove', this.onScrubMove);
        window.removeEventListener('pointerup', this.onScrubEnd);
        this.stopWaitingForInteraction();
      }

      bindEvents() {
        // CTA mode is a muted autoplay loop with no controls: only the viewport observer drives it.
        // No keyboard or pointer handling, and it neither pauses nor gets paused by other players.
        if (this.ctaMode) return;

        this.video.addEventListener('play', this.onPlay);
        this.video.addEventListener('pause', this.onPause);
        this.video.addEventListener('timeupdate', this.onTimeUpdate);
        this.video.addEventListener('progress', this.onProgress);
        this.video.addEventListener('volumechange', this.onVolumeChange);

        if (this.playButton) this.playButton.addEventListener('click', () => this.togglePlay());
        if (this.rewindButton) this.rewindButton.addEventListener('click', () => this.skip(-this.skipBy));
        if (this.forwardButton) this.forwardButton.addEventListener('click', () => this.skip(this.skipBy));
        if (this.volumeButton) this.volumeButton.addEventListener('click', () => this.toggleMute());
        if (this.fullscreenButton) this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
        if (this.poster) this.poster.addEventListener('click', () => this.startFromPoster());
        if (this.unmuteSurface) this.unmuteSurface.addEventListener('click', () => this.unmute());
        if (this.progress) this.progress.addEventListener('pointerdown', this.onScrubStart);

        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('pointermove', this.onPointerActivity);
        this.addEventListener('pointerdown', this.onPointerActivity);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onPointerActivity);

        document.addEventListener('vplayer:play', this.onOtherPlayerStarted);
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      }

      observeViewport() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
        this.observer.observe(this);
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          this.inViewport = entry.isIntersecting;

          if (entry.isIntersecting) {
            this.enteredViewport();
          } else if (!this.video.paused) {
            this.pausedByViewport = true;
            this.video.pause();
          }
        });
      }

      enteredViewport() {
        this.applyPreload();

        if (!this.autoplay || this.userPaused) return;

        // Autoplay is always muted — browsers only allow muted autoplay.
        this.video.muted = true;
        this.attemptPlay();
      }

      // The video renders with preload="none" so a slot hidden at this breakpoint (desktop vs.
      // mobile media) never downloads. The configured preload applies once it is actually seen.
      applyPreload() {
        const preload = this.video.dataset.vpPreload;
        if (!preload) return;

        this.video.preload = preload;
        delete this.video.dataset.vpPreload;
      }

      attemptPlay() {
        const attempt = this.video.play();
        if (!attempt || typeof attempt.catch !== 'function') return;

        attempt.catch(() => {
          this.video.muted = true;
          const retry = this.video.play();
          if (retry && typeof retry.catch === 'function') {
            retry.catch(() => this.onAutoplayBlocked());
          }
        });
      }

      // Low Power Mode (iOS) and Data Saver (Android) block even muted autoplay. Keep the play
      // button as a fallback, and start on the visitor's first tap anywhere on the page.
      onAutoplayBlocked() {
        this.classList.add('is-idle');
        if (!this.autoplay || this.waitingForInteraction) return;

        this.waitingForInteraction = true;
        document.addEventListener('touchend', this.onFirstInteraction, { passive: true });
        document.addEventListener('click', this.onFirstInteraction);
      }

      onFirstInteraction() {
        this.stopWaitingForInteraction();
        if (this.userPaused || !this.inViewport || !this.video.paused) return;

        this.attemptPlay();
      }

      stopWaitingForInteraction() {
        if (!this.waitingForInteraction) return;

        this.waitingForInteraction = false;
        document.removeEventListener('touchend', this.onFirstInteraction);
        document.removeEventListener('click', this.onFirstInteraction);
      }

      startFromPoster() {
        this.applyPreload();
        this.userPaused = false;
        this.classList.remove('is-idle');
        this.attemptPlay();
      }

      togglePlay() {
        if (this.video.paused) {
          this.userPaused = false;
          this.attemptPlay();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      skip(amount) {
        const duration = this.video.duration;
        if (!duration || Number.isNaN(duration)) return;

        this.video.currentTime = Math.min(Math.max(this.video.currentTime + amount, 0), duration);
        this.revealControls();
      }

      toggleMute() {
        this.video.muted = !this.video.muted;
        if (!this.video.muted && this.video.volume === 0) this.video.volume = 1;
      }

      unmute() {
        this.video.muted = false;
        if (this.video.volume === 0) this.video.volume = 1;
      }

      toggleFullscreen() {
        const doc = document;
        const active = doc.fullscreenElement || doc.webkitFullscreenElement;

        if (active) {
          if (doc.exitFullscreen) doc.exitFullscreen();
          else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
          return;
        }

        if (this.frame.requestFullscreen) this.frame.requestFullscreen();
        else if (this.frame.webkitRequestFullscreen) this.frame.webkitRequestFullscreen();
        else if (this.video.webkitEnterFullscreen) this.video.webkitEnterFullscreen();
      }

      onFullscreenChange() {
        const active = document.fullscreenElement || document.webkitFullscreenElement;
        const isActive = active === this.frame;
        this.classList.toggle('is-fullscreen', isActive);

        if (!this.fullscreenButton) return;

        this.fullscreenButton.setAttribute(
          'aria-label',
          isActive ? this.fullscreenButton.dataset.labelExit : this.fullscreenButton.dataset.labelEnter
        );
        this.swapIcon(this.fullscreenButton, isActive ? 'fullscreen-exit' : 'fullscreen');
      }

      onPlay() {
        this.classList.remove('is-idle');
        this.syncPlayState();
        this.scheduleHide();
        document.dispatchEvent(new CustomEvent('vplayer:play', { detail: { player: this } }));
      }

      onPause() {
        this.syncPlayState();
        if (this.hideTimer) clearTimeout(this.hideTimer);

        // Paused only because it scrolled out of view: keep the controls hidden, so autoplay
        // resumes on the way back without flashing them.
        if (this.pausedByViewport) {
          this.pausedByViewport = false;
          this.hideControls();
          return;
        }

        this.revealControls();
      }

      onOtherPlayerStarted(event) {
        if (event.detail.player === this) return;
        if (!this.video.paused) this.video.pause();
      }

      syncPlayState() {
        const paused = this.video.paused;
        if (!this.playButton) return;

        this.playButton.setAttribute(
          'aria-label',
          paused ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause
        );
        this.swapIcon(this.playButton, paused ? 'play' : 'pause');
      }

      swapIcon(button, state) {
        button.querySelectorAll('[data-vp-state]').forEach((icon) => {
          icon.hidden = icon.dataset.vpState !== state;
        });
      }

      syncVolumeState() {
        this.onVolumeChange();
      }

      onVolumeChange() {
        const muted = this.video.muted || this.video.volume === 0;
        this.classList.toggle('is-unmute-available', muted && this.hasAttribute('data-unmute-surface'));

        if (this.volumeButton) {
          this.volumeButton.setAttribute(
            'aria-label',
            muted ? this.volumeButton.dataset.labelUnmute : this.volumeButton.dataset.labelMute
          );
          this.swapIcon(this.volumeButton, muted ? 'volume-off' : 'volume-on');
        }
      }

      onTimeUpdate() {
        if (this.scrubbing) return;
        this.renderProgress();
      }

      renderProgress() {
        const duration = this.video.duration;
        if (!duration || Number.isNaN(duration)) return;

        const ratio = this.video.currentTime / duration;
        if (this.played) this.played.style.transform = `scaleX(${ratio})`;
        if (this.handle) this.handle.style.left = `${ratio * 100}%`;
        if (this.progress) {
          this.progress.setAttribute('aria-valuenow', Math.round(ratio * 100));
        }
      }

      onProgress() {
        const duration = this.video.duration;
        if (!this.buffer || !duration || Number.isNaN(duration) || !this.video.buffered.length) return;

        const end = this.video.buffered.end(this.video.buffered.length - 1);
        this.buffer.style.transform = `scaleX(${end / duration})`;
      }

      onScrubStart(event) {
        this.scrubbing = true;
        this.classList.add('is-scrubbing');
        this.seekToPointer(event);
        window.addEventListener('pointermove', this.onScrubMove);
        window.addEventListener('pointerup', this.onScrubEnd);
      }

      onScrubMove(event) {
        if (!this.scrubbing) return;
        this.seekToPointer(event);
      }

      onScrubEnd() {
        this.scrubbing = false;
        this.classList.remove('is-scrubbing');
        window.removeEventListener('pointermove', this.onScrubMove);
        window.removeEventListener('pointerup', this.onScrubEnd);
      }

      seekToPointer(event) {
        const duration = this.video.duration;
        if (!duration || Number.isNaN(duration)) return;

        const bounds = this.progress.getBoundingClientRect();
        if (!bounds.width) return;

        const ratio = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
        this.video.currentTime = ratio * duration;
        this.renderProgress();
      }

      onKeyDown(event) {
        const onControl = event.target !== this && event.target.closest('.vplayer-button');
        if (onControl && (event.key === ' ' || event.key === 'Enter')) return;

        switch (event.key) {
          case ' ':
          case 'k':
            this.togglePlay();
            break;
          case 'ArrowLeft':
            this.skip(-this.skipBy);
            break;
          case 'ArrowRight':
            this.skip(this.skipBy);
            break;
          case 'ArrowUp':
            this.video.volume = Math.min(this.video.volume + 0.1, 1);
            break;
          case 'ArrowDown':
            this.video.volume = Math.max(this.video.volume - 0.1, 0);
            break;
          case 'm':
            this.toggleMute();
            break;
          case 'f':
            this.toggleFullscreen();
            break;
          default:
            return;
        }

        event.preventDefault();
        this.revealControls();
      }

      onPointerActivity() {
        this.revealControls();
        this.scheduleHide();
      }

      onPointerLeave() {
        if (this.video.paused) return;
        this.hideControls();
      }

      revealControls() {
        this.classList.add('is-controls-visible');
      }

      hideControls() {
        if (this.persistControls) return;
        this.classList.remove('is-controls-visible');
      }

      scheduleHide() {
        if (this.persistControls) return;
        if (this.hideTimer) clearTimeout(this.hideTimer);
        if (this.video.paused) return;

        this.hideTimer = setTimeout(() => this.hideControls(), this.hideDelay);
      }
    }
  );
}
